import { Outlet, Link, useLocation } from "react-router-dom";
import { 
  Briefcase, 
  LayoutDashboard, 
  Plus, 
  Settings, 
  LogOut, 
  CreditCard,
  Sparkles,
  User,
  FileText,
  ArrowLeftRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useUser } from "../context/UserContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useUser();
  
  // Determine which portal we're in based on route
  const isCandidatePortal = ["/empleos", "/candidate", "/jobs/public"].some(
    path => location.pathname.startsWith(path)
  );
  
  // Navigation items for each portal
  const recruiterNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/jobs", label: "Mis Puestos", icon: Briefcase },
  ];
  
  const candidateNavItems = [
    { path: "/empleos", label: "Optimizar CV", icon: Sparkles },
    { path: "/candidate/profile", label: "Mi Perfil", icon: User },
  ];
  
  const navItems = isCandidatePortal ? candidateNavItems : recruiterNavItems;
  
  // Determine active state
  const isActive = (path) => {
    if (path === "/dashboard" && location.pathname === "/dashboard") return true;
    if (path === "/jobs" && location.pathname.startsWith("/jobs") && !location.pathname.includes("/public")) return true;
    if (path === "/empleos" && location.pathname === "/empleos") return true;
    if (path === "/candidate/profile" && location.pathname.startsWith("/candidate")) return true;
    if (location.pathname.startsWith("/jobs/public") && path === "/empleos") return true;
    return false;
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isCandidatePortal ? "bg-accent" : "bg-primary"
              }`}>
                {isCandidatePortal ? (
                  <Sparkles className="w-5 h-5 text-accent-foreground" />
                ) : (
                  <Briefcase className="w-5 h-5 text-primary-foreground" />
                )}
              </div>
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-lg text-foreground">
                  TalentMatcher
                </span>
                <Badge variant="outline" className="ml-2 text-xs">
                  {isCandidatePortal ? "Candidatos" : "Reclutadores"}
                </Badge>
              </div>
            </Link>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Button
                      variant={active ? "secondary" : "ghost"}
                      className="gap-2"
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>
            
            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Portal-specific action button */}
              {isCandidatePortal ? (
                // Candidate: Link to profile if no user
                !user && (
                  <Link to="/candidate/profile" data-testid="create-profile-btn">
                    <Button className="rounded-full gap-2">
                      <User className="w-4 h-4" />
                      <span className="hidden sm:inline">Mi Perfil</span>
                    </Button>
                  </Link>
                )
              ) : (
                // Recruiter: Create Job Button
                <Link to="/jobs/new" data-testid="create-job-btn">
                  <Button className="rounded-full gap-2">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Nuevo Puesto</span>
                  </Button>
                </Link>
              )}
              
              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`rounded-full w-9 h-9 ${
                        isCandidatePortal ? "bg-accent/20" : "bg-secondary"
                      }`}
                      data-testid="user-menu-btn"
                    >
                      <span className="text-sm font-medium">
                        {user.email?.charAt(0).toUpperCase()}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium truncate">{user.email}</p>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {user.plan?.toUpperCase() || "FREE"} Plan
                      </Badge>
                    </div>
                    <DropdownMenuSeparator />
                    
                    {/* Switch Portal */}
                    <DropdownMenuItem asChild>
                      <Link 
                        to={isCandidatePortal ? "/dashboard" : "/empleos"} 
                        className="cursor-pointer"
                      >
                        <ArrowLeftRight className="w-4 h-4 mr-2" />
                        {isCandidatePortal ? "Portal Reclutador" : "Portal Candidato"}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    
                    {!isCandidatePortal && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/billing" className="cursor-pointer">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Facturación
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="cursor-pointer">
                            <Settings className="w-4 h-4 mr-2" />
                            Configuración
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {isCandidatePortal && (
                      <DropdownMenuItem asChild>
                        <Link to="/candidate/profile" className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" />
                          Mi Perfil
                        </Link>
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="cursor-pointer text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                // Switch portal link for non-logged users
                <Link to={isCandidatePortal ? "/dashboard" : "/empleos"}>
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">
                    <ArrowLeftRight className="w-3 h-3" />
                    <span className="hidden sm:inline">
                      {isCandidatePortal ? "Reclutador" : "Candidato"}
                    </span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <Outlet />
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-4 py-2 ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* Settings for recruiter, or switch portal */}
          {isCandidatePortal ? (
            <Link
              to="/dashboard"
              className="flex flex-col items-center gap-1 px-4 py-2 text-muted-foreground"
              data-testid="mobile-nav-switch"
            >
              <ArrowLeftRight className="w-5 h-5" />
              <span className="text-xs font-medium">Reclutador</span>
            </Link>
          ) : (
            <Link
              to="/settings"
              className={`flex flex-col items-center gap-1 px-4 py-2 ${
                location.pathname === "/settings" ? "text-primary" : "text-muted-foreground"
              }`}
              data-testid="mobile-nav-settings"
            >
              <Settings className="w-5 h-5" />
              <span className="text-xs font-medium">Config</span>
            </Link>
          )}
        </div>
      </nav>
      
      {/* Bottom padding for mobile nav */}
      <div className="h-16 md:hidden" />
    </div>
  );
}
